import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  AlertCircle,
  Loader,
  Gift
} from 'lucide-react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useAuthStore } from '@/store/authStore'
import { oauthService } from '@/services/oauth'
import styles from './PotatoPiratesAuth.module.css'

export function PotatoPiratesAuth() {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin')
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password_confirmation: '',
    first_name: '',
    last_name: '',
    referral_code: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [googleBtnVisible, setGoogleBtnVisible] = useState(false)
  const googleBtnRef = useRef<HTMLDivElement>(null)
  const { login, register, isLoading, error, clearError, isAuthenticated } = useAuthStore()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const navigate = useNavigate()

  useEffect(() => {
    // Create matrix rain effect
    const createMatrixRain = () => {
      const matrix = document.getElementById('matrix')
      if (!matrix) return

      const characters = '01ABCDEF{}[]()<>+-*/=POTATO🥔⚓🌊'
      const columns = Math.floor(window.innerWidth / 20)
      
      // Clear existing columns
      matrix.innerHTML = ''
      
      for (let i = 0; i < columns; i++) {
        const column = document.createElement('div')
        column.className = styles.matrixColumn
        column.style.left = i * 20 + 'px'
        column.style.animationDuration = (Math.random() * 3 + 2) + 's'
        column.style.animationDelay = Math.random() * 2 + 's'
        
        let text = ''
        for (let j = 0; j < 30; j++) {
          text += characters.charAt(Math.floor(Math.random() * characters.length)) + '<br>'
        }
        column.innerHTML = text
        
        matrix.appendChild(column)
      }
    }

    createMatrixRain()

    // Recreate on window resize
    const handleResize = () => createMatrixRain()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // Clear error when switching modes
  useEffect(() => {
    clearError()
  }, [mode, clearError])

  // Initialize and render Google Sign-In button
  useEffect(() => {
    let cancelled = false

    const setupGoogle = async () => {
      try {
        await oauthService.initializeGoogle()
        if (cancelled || !googleBtnRef.current) return

        // Wait one frame so the container has layout dimensions
        await new Promise<void>(r => requestAnimationFrame(() => r()))
        if (cancelled || !googleBtnRef.current) return

        await oauthService.renderGoogleButton('google-signin-btn', {
          theme: 'filled_black',
          shape: 'rectangular',
          text: 'continue_with',
          size: 'large',
        })

        // Verify the button was actually injected into the container
        if (!cancelled && googleBtnRef.current && googleBtnRef.current.childElementCount > 0) {
          setGoogleBtnVisible(true)
        }
      } catch {
        // Google Sign-In unavailable
      }
    }

    setupGoogle()
    return () => { cancelled = true }
  }, [])

  // Redirect after successful authentication
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()

    try {
      // Get reCAPTCHA token if available (won't be if key isn't configured)
      const captchaToken = executeRecaptcha
        ? await executeRecaptcha(mode === 'signin' ? 'login' : 'register')
        : undefined

      if (mode === 'signin') {
        await login(formData.email, formData.password, rememberMe, captchaToken)
        // Redirect will be handled by useEffect when isAuthenticated changes
      } else {
        await register({
          email: formData.email,
          password: formData.password,
          password_confirmation: formData.password_confirmation,
          first_name: formData.first_name,
          last_name: formData.last_name,
          referral_code: formData.referral_code || undefined
        }, captchaToken)
        // Navigate to verification page
        navigate(`/auth/verify?email=${encodeURIComponent(formData.email)}`, { replace: true })
      }
    } catch {
      // Error is handled by the auth store
    }
  }

  const isSignIn = mode === 'signin'

  return (
    <div className={styles.potatoPiratesAuth}>
      {/* Background Effects */}
      <div className={styles.deepSeaBg}></div>
      <div className={styles.matrixBg} id="matrix"></div>
      <div className={styles.scanlines}></div>
      <div className={styles.noiseOverlay}></div>

      {/* Back to Home Link */}
      <div className={styles.backHome}>
        <Link to="/" className={styles.backLink}>
          ← Back to Home
        </Link>
      </div>

      {/* Auth Container */}
      <div className={styles.authContainer}>
        <div className="auth-content">
          {/* Logo */}
          <div className={styles.logo}>
            <h1>🥔⚓ POTATO PIRATES:</h1>
            <h2 className={styles.authTitle}>THE ARMADA</h2>
            <p>Interactive Learning Platform</p>
          </div>

          {/* Mode Toggle */}
          <div className={styles.modeToggle}>
            <button
              onClick={() => setMode('signin')}
              className={`${styles.modeBtn} ${isSignIn ? styles.active : ''}`}
            >
              SIGN IN
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`${styles.modeBtn} ${!isSignIn ? styles.active : ''}`}
            >
              REGISTER
            </button>
          </div>

          {/* Header */}
          {/* <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={styles.authHeader}
          >
            <h2 className={styles.authTitle}>
              {isSignIn ? 'WELCOME BACK PIRATE' : 'JOIN THE ARMADA'}
            </h2>
            <p className={styles.authSubtitle}>
              {isSignIn 
                ? 'Access your learning dashboard'
                : 'Begin your educational adventure'
              }
            </p>
          </motion.div> */}

          {/* Error/Success Messages */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, height: 0 }}
                animate={{ opacity: 1, scale: 1, height: 'auto' }}
                exit={{ opacity: 0, scale: 0.95, height: 0 }}
                className={styles.errorMessage}
              >
                <AlertCircle className="w-5 h-5" />
                <div>
                  <span>{error}</span>
                  {isSignIn && error.toLowerCase().includes('invalid') && (
                    <p className="mt-1.5 text-sm opacity-90">
                      Don't have an account?{' '}
                      <button
                        type="button"
                        onClick={() => { clearError(); setMode('signup') }}
                        className="underline bg-transparent border-none text-inherit cursor-pointer p-0 font-[inherit]"
                      >
                        Sign up here
                      </button>
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onSubmit={handleSubmit}
            className={styles.authForm}
          >
            {/* Name Fields (Sign Up only) */}
            {!isSignIn && (
              <div className={styles.nameFields}>
                <div className={styles.formGroup}>
                  <label>FIRST NAME</label>
                  <div className={styles.inputContainer}>
                    <User className={styles.inputIcon} />
                    <input
                      type="text"
                      required
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      placeholder="Enter first name"
                    />
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label>LAST NAME</label>
                  <div className={styles.inputContainer}>
                    <User className={styles.inputIcon} />
                    <input
                      type="text"
                      required
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      placeholder="Enter last name"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Email */}
            <div className={styles.formGroup}>
              <label>EMAIL</label>
              <div className={styles.inputContainer}>
                <Mail className={styles.inputIcon} />
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {/* Password */}
            <div className={styles.formGroup}>
              <label>PASSWORD</label>
              <div className={styles.inputContainer}>
                <Lock className={styles.inputIcon} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={styles.passwordToggle}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Confirm Password (Sign Up only) */}
            {!isSignIn && (
              <div className={styles.formGroup}>
                <label>CONFIRM PASSWORD</label>
                <div className={styles.inputContainer}>
                  <Lock className={styles.inputIcon} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password_confirmation}
                    onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
                    placeholder="Confirm your password"
                  />
                </div>
              </div>
            )}
            {/* Referral Code (Sign Up only) */}
            {!isSignIn && (
              <div className={styles.formGroup}>
                <label>REFERRAL CODE (OPTIONAL)</label>
                <div className={styles.inputContainer}>
                  <Gift className={styles.inputIcon} />
                  <input
                    type="text"
                    value={formData.referral_code}
                    onChange={(e) => setFormData({ ...formData, referral_code: e.target.value.toUpperCase() })}
                    placeholder="Enter referral code"
                    maxLength={8}
                  />
                </div>
              </div>
            )}

            {/* Remember Me & Forgot Password (Sign In only) */}
            {isSignIn && (
              <div className={`${styles.rememberMe} flex justify-between items-center`}>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="remember"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <label htmlFor="remember">Remember me for 30 days</label>
                </div>
                <Link
                  to="/auth/forgot-password"
                  className="text-theme-secondary text-xs no-underline opacity-80"
                >
                  Forgot password?
                </Link>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={styles.submitBtn}
            >
              {isLoading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>{isSignIn ? 'AUTHENTICATING...' : 'REGISTERING...'}</span>
                </>
              ) : (
                <span>{isSignIn ? 'AUTHENTICATE' : 'JOIN'}</span>
              )}
            </button>
          </motion.form>

          {/* OAuth Divider + Google Sign-In */}
          <div className={styles.oauthSection}>
            {googleBtnVisible && (
              <div className={styles.oauthDivider}>
                <span>OR</span>
              </div>
            )}
            <div
              id="google-signin-btn"
              ref={googleBtnRef}
              className={styles.googleBtn}
            />
          </div>

          {/* Guest Login */}
          <div className={styles.guestLogin}>
            <p>Joining a course as a guest?</p>
            <Link to="/join" className={styles.guestLink}>
              Enter Lesson Code →
            </Link>
          </div>
        </div>
      </div>

    </div>
  )
}